import { create } from "zustand";

type ThemePreference = "system" | "light" | "dark";
type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "talent-theme-preference";
const CAMPAIGN_SORT_KEY = "talent-campaign-sort";

export type CampaignSortBy =
  | "RECENTLY_ACCESSED"
  | "RECENTLY_UPDATED"
  | "TITLE"
  | "CLIENT"
  | "STATUS"
  | "STAGE";
export type SortDirection = "ASC" | "DESC";
export type CampaignSort = { sortBy: CampaignSortBy; sortDirection: SortDirection };

const DEFAULT_CAMPAIGN_SORT: CampaignSort = {
  sortBy: "RECENTLY_ACCESSED",
  sortDirection: "DESC",
};

const VALID_SORT_BY: CampaignSortBy[] = [
  "RECENTLY_ACCESSED",
  "RECENTLY_UPDATED",
  "TITLE",
  "CLIENT",
  "STATUS",
  "STAGE",
];
const VALID_DIRECTION: SortDirection[] = ["ASC", "DESC"];

function loadCampaignSort(): CampaignSort {
  try {
    const raw = localStorage.getItem(CAMPAIGN_SORT_KEY);
    if (!raw) return DEFAULT_CAMPAIGN_SORT;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      VALID_SORT_BY.includes(parsed.sortBy) &&
      VALID_DIRECTION.includes(parsed.sortDirection)
    ) {
      return { sortBy: parsed.sortBy, sortDirection: parsed.sortDirection };
    }
  } catch {
    // fall through
  }
  return DEFAULT_CAMPAIGN_SORT;
}

function getSystemTheme(): EffectiveTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference: ThemePreference): EffectiveTheme {
  return preference === "system" ? getSystemTheme() : preference;
}

function applyTheme(theme: EffectiveTheme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  themePreference: ThemePreference;
  effectiveTheme: EffectiveTheme;
  setThemePreference: (pref: ThemePreference) => void;
  campaignSort: CampaignSort;
  setCampaignSort: (sort: CampaignSort) => void;
}

const savedPreference = (localStorage.getItem(STORAGE_KEY) as ThemePreference) || "system";

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  themePreference: savedPreference,
  effectiveTheme: resolveTheme(savedPreference),

  setThemePreference: (pref: ThemePreference) => {
    localStorage.setItem(STORAGE_KEY, pref);
    const effective = resolveTheme(pref);
    applyTheme(effective);
    set({ themePreference: pref, effectiveTheme: effective });
  },

  campaignSort: loadCampaignSort(),
  setCampaignSort: (sort: CampaignSort) => {
    localStorage.setItem(CAMPAIGN_SORT_KEY, JSON.stringify(sort));
    set({ campaignSort: sort });
  },
}));

// Listen for system theme changes when preference is "system"
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  const { themePreference } = useUIStore.getState();
  if (themePreference === "system") {
    const effective = getSystemTheme();
    applyTheme(effective);
    useUIStore.setState({ effectiveTheme: effective });
  }
});
